// 3rd party
import React from "react";
import moment from "moment";
import { FormattedMessage, FormattedDate, useIntl, defineMessages } from "react-intl";
import { Grid, Tooltip } from "@material-ui/core";
import { InfoOutlined as NotificationIcon } from "@material-ui/icons";

// components
import { Card } from "../common/card/card";
import { Header } from "../common/title/header";
import { useWorkOrderStyles, Caption, Value, CardTitle } from "./workOrderDetails";

// helper functions
import { STATUSES } from "../../globals/workOrderStatus";
import { useLocalizedName } from "../../globals/langHelpers";
import { phoneNumberFormatterValue } from "../common/dataList/dataListHelper";
import WarningIcon from "../common/icons/WarningIcon";

const messages = defineMessages({
    rapReason: {
        id: "admin.work-order-details.rap-reason-label",
        defaultMessage: "RAP REASON: ",
        description: "reason for Rap Request as small tooltip",
    },
});

const WorkOrderJobOverviewInfo = ({ workOrderData, vendorManager, relatedIdInformation, cancelledReason }) => {
    const classes = useWorkOrderStyles();
    const { getLocalizedName } = useLocalizedName();
    const formateDate = (iDate) => (iDate ? moment.utc(iDate).add(-moment().utcOffset(), "minutes").format("YYYY-MM-DDTHH:mm:ss.SSSSZ") : "");

    const intl = useIntl();
    const rapReasonString =
        getLocalizedName({ object: workOrderData.reasonCode }) +
        (workOrderData?.subReasonCode && getLocalizedName({ object: workOrderData?.subReasonCode })
            ? " - " + getLocalizedName({ object: workOrderData?.subReasonCode })
            : "");
    const vendorManagerReady = vendorManager ? !(Object.keys(vendorManager).length === 0) : false;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Header>
                    <FormattedMessage
                        id="admin.work-order-details.wo-info.job-overview-title"
                        defaultMessage="Job overview"
                        description="Admin work order Job overview title"
                    />
                </Header>
            </Grid>
            <Grid item xs={5}>
                <Card>
                    <CardTitle>
                        <FormattedMessage
                            id="admin.work-order-details.wo-info.schedule-title"
                            defaultMessage="Schedule"
                            description="Admin work order Schedule title"
                        />
                    </CardTitle>
                    <Grid container className={classes.row}>
                        <Grid item xs={6}>
                            <Caption>
                                <FormattedMessage
                                    id="admin.work-order-details.wo-info.overview.orig-due-date-caption"
                                    defaultMessage="Original due date"
                                    description="Admin work order Original due date caption"
                                />
                            </Caption>
                            <Value>
                                {workOrderData.originalDueDate ? moment.utc(workOrderData.originalDueDate).local().format("MMM DD, YYYY") : null}
                            </Value>
                        </Grid>
                        <Grid item xs={6}>
                            <Caption>
                                <FormattedMessage
                                    id="admin.work-order-details.wo-info.overview.appt-due-date-caption"
                                    defaultMessage="Appointment due date"
                                    description="Admin work order Appointment due date caption"
                                />
                                {workOrderData.status && workOrderData.status.id === STATUSES.RAP_REQUESTED && (
                                    <Tooltip classes={{ tooltip: classes.tooltip }} title={intl.formatMessage(messages.rapReason) + rapReasonString}>
                                        <NotificationIcon classes={{ root: classes.informationIcon }} />
                                    </Tooltip>
                                )}
                            </Caption>
                            <Value>
                                {relatedIdInformation?.tasks?.appointmentDueDate
                                    ? moment.utc(new Date(relatedIdInformation?.tasks?.appointmentDueDate))?.format("MMM DD, YYYY")
                                    : null}
                            </Value>
                        </Grid>
                    </Grid>
                    <Grid container className={classes.row}>
                        <Grid item xs={6}>
                            <Caption>
                                <FormattedMessage
                                    id="admin.work-order-details.wo-info.overview.schd-datetime-caption"
                                    defaultMessage="Scheduled date \u0026 time"
                                    description="Admin work order Scheduled datetime caption"
                                />
                            </Caption>
                            <Value>
                                {relatedIdInformation?.tasks?.scheduledDate
                                    ? moment.utc(new Date(relatedIdInformation?.tasks?.scheduledDate)).format("MMM DD, YYYY HH:mm:ss")
                                    : null}
                            </Value>
                        </Grid>
                        <Grid item xs={6}>
                            <Caption>
                                <FormattedMessage
                                    id="admin.work-order-details.wo-info.overview.created-on-caption"
                                    defaultMessage="Created on"
                                    description="Admin work order Created on caption"
                                />
                            </Caption>
                            <Value>
                                {relatedIdInformation.createdDate
                                    ? moment.utc(new Date(relatedIdInformation?.createdDate)).format("MMM DD, YYYY HH:mm")
                                    : null}
                            </Value>
                        </Grid>
                    </Grid>
                </Card>
            </Grid>
            {relatedIdInformation?.tasks?.workType?.displayLocationInfo && (
                <Grid item xs={7}>
                    <Card>
                        <CardTitle>
                            <FormattedMessage
                                id="admin.work-order-details.wo-info.click-info-title"
                                defaultMessage="Other information"
                                description="Admin work order Other information title"
                            />
                        </CardTitle>
                        <Grid container className={classes.row}>
                            <Grid item xs={4}>
                                <Caption>
                                    <FormattedMessage
                                        id="admin.work-order-details.wo-info.click.click-dispatch-id-caption"
                                        defaultMessage="Click dispatch ID"
                                        description="Admin work order Click dispatch ID caption"
                                    />
                                </Caption>
                                <Value>{workOrderData.clickDispatchId}</Value>
                            </Grid>
                            <Grid item xs={4}>
                                <Caption>
                                    <FormattedMessage
                                        id="admin.work-order-details.wo-info.click.click-region-caption"
                                        defaultMessage="Click region"
                                        description="Admin work order Click region caption"
                                    />
                                </Caption>
                                <Value>{workOrderData.region}</Value>
                            </Grid>
                            <Grid item xs={4}>
                                <Caption>
                                    <FormattedMessage
                                        id="admin.work-order-details.wo-info.overview.contractor-caption"
                                        defaultMessage="Contractor"
                                        description="Admin work order Contractor caption"
                                    />
                                </Caption>
                                <Value>
                                    {workOrderData.contractorName} ({workOrderData.contractNumber})
                                </Value>
                            </Grid>
                        </Grid>
                        <Grid container className={classes.row}>
                            <Grid item xs={4}>
                                <Caption>
                                    <FormattedMessage
                                        id="admin.work-order-details.wo-info.click.click-district-caption"
                                        defaultMessage="Click district"
                                        description="Admin work order Click district caption"
                                    />
                                </Caption>
                                <Value>{workOrderData.district}</Value>
                            </Grid>
                            <Grid item xs={4}>
                                <Caption>
                                    <FormattedMessage
                                        id="admin.work-order-details.wo-info.click.clli-caption"
                                        defaultMessage="CLLI"
                                        description="Admin work order CLLI caption"
                                    />
                                </Caption>
                                <Value>{workOrderData.zone}</Value>
                            </Grid>
                            <Grid item xs={4}>
                                <Caption>
                                    <FormattedMessage
                                        id="admin.work-order-details.wo-info.overview.created-by-caption"
                                        defaultMessage="Created by"
                                        description="Admin work order Created by caption"
                                    />
                                </Caption>
                                <Value>{relatedIdInformation.technicianId}</Value>
                            </Grid>
                        </Grid>
                        {process.env.REACT_APP_FEATURE_CMISR3 === "true" && (
                            <Grid container className={classes.row}>
                                <Grid item xs={4}>
                                    <Caption>
                                        <FormattedMessage
                                            id="admin.work-order-details.wo-info.click.vendor-manger-caption"
                                            defaultMessage="Vendor manager"
                                            description="Admin work order vendor manager caption"
                                        />
                                    </Caption>
                                    <Value>{vendorManagerReady ? `${vendorManager.firstName} ${vendorManager.lastName}` : ""}</Value>
                                </Grid>
                                <Grid item xs={4}>
                                    <Caption>
                                        <FormattedMessage
                                            id="admin.work-order-details.wo-info.click.vendor-manger-phone-number-caption"
                                            defaultMessage="Phone number"
                                            description="Admin work order phone number"
                                        />
                                    </Caption>
                                    <Value>{vendorManagerReady ? phoneNumberFormatterValue(vendorManager.phoneNumber) : ""}</Value>
                                </Grid>
                                <Grid item xs={4}>
                                    <Caption>
                                        <FormattedMessage
                                            id="admin.work-order-details.wo-info.click.vendor-manger-email-caption"
                                            defaultMessage="Email"
                                            description="Admin work order email"
                                        />
                                    </Caption>
                                    <Value>{vendorManagerReady ? vendorManager.email : ""}</Value>
                                </Grid>
                            </Grid>
                        )}
                    </Card>
                </Grid>
            )}
        </Grid>
    );
};

export default WorkOrderJobOverviewInfo;
